import { useEffect, useRef, useState } from "react";
import { X, PackageX, Clock, Truck, ShoppingBag, Undo2, ArrowLeftRight, Package, Gift } from "lucide-react";
import api, { cachedGet, invalidateCache } from "@/lib/api";

/**
 * DashboardNotifications.jsx
 * ------------------------------------------------------------------
 * Drop this into Dashboard.jsx, rendered once, right below the existing
 * "Instagram disconnected" / "Free plan" banners and above the tab bar.
 *
 *   <DashboardNotifications merchantReady={!!me} refreshTick={notificationsTick} />
 *
 * Backed by GET /api/merchant/notifications and
 * DELETE /api/merchant/notifications?id=<id>.
 *
 * Deliberately NOT a bell/dropdown -- renders as the same small dismissible
 * banner-card style already used elsewhere in Dashboard.jsx (see the
 * "Instagram disconnected" banner), just one card per active notification.
 * Dismissing (x) permanently deletes the row -- it does not just mark it
 * read, since these are meant to be single-fire ("show once") per the
 * dedup built into the backend (one active row per order / per product+size).
 *
 * ------------------------------------------------------------------
 * Fix notes (multi-click dismiss bug)
 * ------------------------------------------------------------------
 * The previous version tracked a single `dismissing` id in state and used
 * `if (dismissing) return;` as a re-entrancy guard. That had three real
 * bugs, any one of which could make the (x) button need 2+ clicks:
 *
 *  1. Falsy-id guard: `if (dismissing) return` treats a notification id of
 *     0 (or "0") as "nothing is dismissing", so a second click on THAT row
 *     -- or any row, while it's mid-flight -- could fire a second DELETE
 *     and race with the first.
 *  2. Single global `dismissing` value meant clicking a second
 *     notification while a first was still in-flight silently did nothing
 *     (blocked by the guard) even though the two are unrelated rows --
 *     it read as "my click didn't work."
 *  3. No client-side memory of what's already been dismissed. If the GET
 *     result was ever re-applied after a dismiss (e.g. a retry, a second
 *     mount, or the backend momentarily still returning the row on a
 *     read-replica), the just-deleted notification would silently
 *     reappear, and the merchant would have to click it again.
 *
 * This version tracks in-flight ids as a Set (so unrelated rows never
 * block each other), keeps a ref of every id ever dismissed this session
 * and filters it out of any future state update no matter where that
 * update comes from, and retries the DELETE once automatically before
 * rolling back -- so a single genuine network hiccup resolves itself
 * without the merchant needing to click again.
 *
 * ------------------------------------------------------------------
 * refreshTick prop
 * ------------------------------------------------------------------
 * `cachedGet` caches by exact URL and `invalidateCache` just clears that
 * cache entry -- it doesn't push any update to components that already
 * fetched and rendered. So an action elsewhere on the dashboard (e.g.
 * marking an order shipped, which hard-deletes its order_unshipped row
 * server-side) needs a way to tell this already-mounted component "go
 * fetch again." `refreshTick` is that signal: bump it from the parent
 * (same pattern as StuckPanel's `onResolved` bumping Dashboard's
 * `statsTick`) any time something outside this component changes what
 * the notifications list should show. Combine with an `invalidateCache`
 * call at the source of the change so this refetch actually hits the
 * network instead of replaying a stale cache entry.
 * ------------------------------------------------------------------
 */

// Full class names are written out on purpose -- Tailwind only generates
// classes it can find as complete strings in the source.
const VIOLET = {
  card: "border-violet-300 bg-violet-50",
  chip: "bg-violet-100 text-violet-700",
  title: "text-violet-800",
};

const CATEGORIES = {
  new_order: {
    icon: ShoppingBag,
    card: "border-emerald-300 bg-emerald-50",
    chip: "bg-emerald-100 text-emerald-700",
    title: "text-emerald-800",
  },
  return: {
    icon: Undo2,
    card: "border-red-300 bg-red-50",
    chip: "bg-red-100 text-red-700",
    title: "text-red-800",
  },
  exchange: { icon: ArrowLeftRight, ...VIOLET },
  referral: {
    icon: Gift,
    card: "border-pink-300 bg-pink-50",
    chip: "bg-pink-100 text-pink-700",
    title: "text-pink-800",
  },
  ship_replacement: {
    icon: Truck,
    card: "border-fuchsia-300 bg-fuchsia-50",
    chip: "bg-fuchsia-100 text-fuchsia-700",
    title: "text-fuchsia-800",
  },
  courier: {
    icon: Package,
    card: "border-sky-300 bg-sky-50",
    chip: "bg-sky-100 text-sky-700",
    title: "text-sky-800",
  },
  unshipped: {
    icon: Clock,
    card: "border-amber-300 bg-amber-50",
    chip: "bg-amber-100 text-amber-700",
    title: "text-amber-800",
  },
  low_stock: {
    icon: PackageX,
    card: "border-orange-300 bg-orange-50",
    chip: "bg-orange-100 text-orange-700",
    title: "text-orange-800",
  },
  fallback: {
    icon: Clock,
    card: "border-ink/10 bg-white",
    chip: "bg-ink/5 text-ink/60",
    title: "text-ink/50",
  },
};

// Known types are matched exactly; the rest fall back to the title text.
// Order matters: more specific checks come first.
function getCategory(n) {
  const type = (n.type || "").toLowerCase();
  const title = (n.title || "").toLowerCase();
  if (type === "referral_reward") return CATEGORIES.referral;
  if (type === "exchange_ship_replacement" || title.includes("replacement")) return CATEGORIES.ship_replacement;
  if (type === "low_stock" || title.includes("low stock")) return CATEGORIES.low_stock;
  if (type === "order_unshipped" || title.includes("unshipped")) return CATEGORIES.unshipped;
  if (title.includes("exchange")) return CATEGORIES.exchange;
  if (title.includes("return")) return CATEGORIES.return;
  if (title.includes("courier") || title.includes("pickup") || title.includes("shipment")) return CATEGORIES.courier;
  if (title.includes("paid") || title.includes("new order")) return CATEGORIES.new_order;
  return CATEGORIES.fallback;
}

export default function DashboardNotifications({ merchantReady, refreshTick = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissingIds, setDismissingIds] = useState(() => new Set());
  // Ids the merchant has already dismissed this session -- once true,
  // always filtered out, regardless of what any later fetch returns.
  const dismissedRef = useRef(new Set());

  const applyIncoming = (incoming) =>
    (incoming || []).filter((n) => !dismissedRef.current.has(n.id));

  useEffect(() => {
    if (!merchantReady) return;
    setLoading(true);
    cachedGet("/merchant/notifications")
      .then((r) => setItems(applyIncoming(r.data.items)))
      .catch(() => {
        // Notifications are non-critical -- dashboard still renders fine without them.
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantReady, refreshTick]);

  const deleteOnce = (id) => api.delete(`/merchant/notifications?id=${encodeURIComponent(id)}`);

  const dismiss = async (id) => {
    if (dismissingIds.has(id)) return; // already in flight for this row specifically

    // Remember this id forever (this session), then remove it from view
    // right away -- and keep it out even if a stale response comes back later.
    dismissedRef.current.add(id);
    setItems((it) => it.filter((x) => x.id !== id));
    setDismissingIds((prev) => new Set(prev).add(id));

    const clearInFlight = () =>
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

    try {
      try {
        await deleteOnce(id);
      } catch (firstErr) {
        // One silent retry -- covers the common case of a transient
        // network/auth blip so the merchant doesn't have to click again.
        await deleteOnce(id);
      }
      invalidateCache("/merchant/notifications");
    } catch (e) {
      // Both attempts failed -- this one genuinely didn't delete on the
      // backend. Un-dismiss it locally and refetch fresh state (rather
      // than trusting a stale local rollback) so it's honest about
      // still being there.
      dismissedRef.current.delete(id);
      invalidateCache("/merchant/notifications");
      cachedGet("/merchant/notifications")
        .then((r) => setItems(applyIncoming(r.data.items)))
        .catch(() => {});
    } finally {
      clearInFlight();
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="mt-4 space-y-2" data-testid="dashboard-notifications">
      {items.map((n) => {
        const cat = getCategory(n);
        const Icon = cat.icon;
        const isDismissing = dismissingIds.has(n.id);
        // Defensive fallback: a notification row with no title/message text
        // (a backend data gap, not a UI bug) used to render as a blank
        // white card -- still dismissible, but with nothing to read. Show
        // a generic line instead of leaving it empty.
        const title = n.title?.trim() || "Needs your attention";
        const message = n.message?.trim() || "Check your Orders or Catalog tab for details.";
        return (
          <div
            key={n.id}
            className={`flex items-center justify-between gap-4 rounded-2xl border p-4 text-ink ${cat.card}`}
            data-testid={`notification-${n.id}`}
          >
            <div className="flex items-center gap-3">
              <span className={`grid h-9 w-9 flex-none place-items-center rounded-xl ${cat.chip}`}>
                <Icon size={16} />
              </span>
              <div>
                <p className={`text-xs font-bold uppercase tracking-[0.15em] ${cat.title}`}>{title}</p>
                <p className="mt-0.5 text-sm text-ink/80">{message}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => dismiss(n.id)}
              disabled={isDismissing}
              title="Dismiss"
              data-testid={`notification-dismiss-${n.id}`}
              className="grid h-8 w-8 flex-none place-items-center rounded-full text-ink/40 hover:bg-ink/5 hover:text-ink disabled:opacity-40"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
